import * as AST from "@effect/schema/AST";
import { make } from "@effect/schema/Schema";
import * as Arr from "effect/Array";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import { constVoid, flow, pipe } from "effect/Function";
import * as Layer from "effect/Layer";
import * as Number from "effect/Number";
import * as Option from "effect/Option";
import * as Predicate from "effect/Predicate";
import * as Record from "effect/Record";
import * as Kysely from "kysely";
import { Config, createRuntime, defaultConfig } from "./Config.js";
import { NanoIdGenerator } from "./Crypto.js";
import { createSocket } from "./Socket.js";
import { DbFactory, deserializeQuery, emptyRows, queryResultFromRows, serializeQuery, } from "./Db.js";
import { applyPatches } from "./Diff.js";
import { makeUnexpectedError } from "./Error.js";
import { AppState, FlushSync } from "./Platform.js";
import { isSqlMutation, } from "./Sqlite.js";
import { makeStore } from "./Store.js";
import { initialSyncState } from "./Sync.js";
export class EvoluFactory extends Context.Tag("EvoluFactory")() {
}
EvoluFactory.Common = Layer.effect(EvoluFactory, Effect.gen(function* () {
    const flushSync = yield* Effect.map(Effect.serviceOption(FlushSync), Option.getOrElse(() => (callback) => callback()));
    const context = Context.empty().pipe(Context.add(DbFactory, yield* DbFactory), Context.add(NanoIdGenerator, yield* NanoIdGenerator), Context.add(FlushSync, flushSync), Context.add(AppState, yield* AppState));
    // For hot/live reloading and future Evolu dynamic import.
    const instances = new Map();
    return EvoluFactory.of({
        createEvolu: (schema, { indexes, initialData, mnemonic, ...config } = {}) => {
            const runtime = createRuntime(config);
            const name = config?.name || defaultConfig.name;
            const dbSchema = {
                tables: schemaToTables(schema),
                indexes: indexes || [],
            };
            let evolu = instances.get(name);
            if (evolu == null) {
                evolu = createEvolu(dbSchema, runtime, initialData, mnemonic).pipe(Effect.provide(context), runtime.runSync);
                instances.set(name, evolu);
            }
            else {
                evolu.ensureSchema(dbSchema);
            }
            return evolu;
        },
    });
}));
const schemaToTables = (schema) => pipe(getPropertySignatures(schema), Record.toEntries, Arr.map(([name, schema]) => ({
    name,
    columns: Object.keys(getPropertySignatures(schema)),
})));
// TODO: Simplify.
// https://discord.com/channels/795981131316985866/1218626687546294386/1218796529725476935
const getPropertySignatures = (schema) => {
    const out = {};
    const propertySignatures = AST.getPropertySignatures(schema.ast);
    for (let i = 0; i < propertySignatures.length; i++) {
        const propertySignature = propertySignatures[i];
        out[propertySignature.name] = make(propertySignature.type);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return out;
};
const createEvolu = (schema, runtime, initialData, mnemonic) => Effect.gen(function* () {
    yield* Effect.logTrace("EvoluFactory createEvolu");
    const config = yield* Config;
    const dbFactory = yield* DbFactory;
    const appState = yield* AppState;
    const nanoIdGenerator = yield* NanoIdGenerator;
    const flushSync = yield* FlushSync;
    const db = yield* dbFactory.createDb;
    const errorStore = yield* makeStore(null);
    const ownerStore = yield* makeStore(null);
    const rowsStore = yield* makeStore(new Map());
    const syncStateStore = yield* makeStore(initialSyncState);
    const loadingPromises = new Map();
    const subscribedQueries = new Map();
    const handleAllErrors = (effect) => effect.pipe(Effect.catchAllDefect((error) => Effect.fail(makeUnexpectedError(error))), Effect.tapError(Effect.logError), Effect.tapError(errorStore.setState));
    const runFork = flow(handleAllErrors, runtime.runFork);
    const runSync = flow(handleAllErrors, runtime.runSync);
    const runPromise = flow(handleAllErrors, runtime.runPromise);
    const initialDataAsMutations = yield* Effect.provideService(initialDataToMutations(initialData), NanoIdGenerator, nanoIdGenerator);
    const handleDbError = (error) => {
        Effect.fail(error).pipe(runFork);
    };
    const handleSyncStateChange = (state) => {
        Effect.logDebug(["Evolu handleSyncStateChange", { state }]).pipe(Effect.zipRight(syncStateStore.setState(state)), runFork);
    };
    const handleDbReceive = () => {
        Effect.gen(function* () {
            yield* Effect.logTrace("Evolu handleDbReceive");
            releaseUnsubscribedLoadingPromises();
            const queries = [...subscribedQueries.keys()];
            if (queries.length > 0) {
                yield* Effect.flatMap(db.loadQueries(queries), handlePatches());
            }
        }).pipe(runFork);
    };
    const sync = ({ refreshQueries }) => () => {
        Effect.flatMap(db.sync(refreshQueries ? [...subscribedQueries.keys()] : []), handlePatches()).pipe(runFork);
    };
    db.init(schema, initialDataAsMutations, handleDbError, handleSyncStateChange, handleDbReceive, mnemonic).pipe(Effect.tap(sync({ refreshQueries: false })), Effect.flatMap(ownerStore.setState), Effect.catchTag("NotSupportedPlatformError", () => Effect.void), // no-op
    runFork);
    let socketResolved = false;
    const socket = new Promise((resolve) => {
        ownerStore.subscribe(() => {
            const owner = ownerStore.getState();
            if (!socketResolved && typeof owner?.id === "string") {
                socketResolved = true;
                resolve(createSocket(sync, config, owner));
            }
        });
    });
    const appStateReset = yield* appState.init({
        onRequestSync: sync({ refreshQueries: true }),
        reloadUrl: config.reloadUrl,
    });
    const handlePatches = (options) => (patches) => Effect.logDebug(["Evolu handlePatches", { patches }]).pipe(Effect.zipRight(rowsStoreStateFromPatches(patches)), Effect.tap((nextState) => Effect.forEach(patches, ({ query }) => resolveLoadingPromises(query, nextState.get(query) || emptyRows()))), Effect.tap((nextState) => {
        if (options?.flushSync) {
            flushSync(() => {
                rowsStore.setState(nextState).pipe(runSync);
            });
        }
        else {
            rowsStore.setState(nextState).pipe(runSync);
        }
    }));
    const rowsStoreStateFromPatches = (patches) => Effect.sync(() => {
        const rowsStoreState = rowsStore.getState();
        if (patches.length === 0)
            return rowsStoreState;
        const queriesRows = Arr.map(patches, ({ query, patches }) => [
            query,
            applyPatches(patches, rowsStoreState.get(query) || emptyRows()),
        ]);
        return new Map([...rowsStoreState, ...queriesRows]);
    });
    const resolveLoadingPromises = (query, rows) => Effect.sync(() => {
        const loadingPromise = loadingPromises.get(query);
        if (!loadingPromise)
            return;
        const result = queryResultFromRows(rows);
        if (loadingPromise.promise.status !== "fulfilled") {
            loadingPromise.resolve(result);
        }
        else {
            // A promise can't be fulfilled 2x, so we need a new one.
            loadingPromise.promise = Promise.resolve(result);
        }
        /**
         * "For example, a data framework can set the status and value fields on
         * a promise preemptively, before passing to React, so that React can
         * unwrap it without waiting a microtask."
         * https://github.com/acdlite/rfcs/blob/first-class-promises/text/0000-first-class-support-for-promises.md
         */
        Object.assign(loadingPromise.promise, {
            status: "fulfilled",
            value: result,
        });
        if (loadingPromise.releaseOnResolve) {
            loadingPromises.delete(query);
        }
    });
    /**
     * We can't delete loading promises in `resolveLoadingPromises` because they
     * must be cached, so repeated calls to `loadQuery` will always return the
     * same promise until the data changes, and we also can't cache them forever
     * because only subscribed queries are automatically updated (reactivity is
     * expensive) hence this function must be called manually on any mutation.
     */
    const releaseUnsubscribedLoadingPromises = () => {
        [...loadingPromises.entries()]
            .filter(([query]) => !subscribedQueries.has(query))
            .forEach(([query, loadingPromise]) => {
            if (loadingPromise.promise.status === "fulfilled") {
                loadingPromises.delete(query);
            }
            else {
                loadingPromise.releaseOnResolve = true;
            }
        });
    };
    const mutate = (() => {
        let queue = [];
        return (table, { id, ...values }, onComplete) => {
            Effect.logDebug(["Evolu mutate", { table, id, values }]).pipe(runSync);
            const isInsert = id == null;
            if (isInsert)
                id = nanoIdGenerator.rowId.pipe(runSync);
            queue = [...queue, [{ table, id, values, isInsert }, onComplete]];
            if (queue.length === 1)
                queueMicrotask(() => {
                    const [mutations, onCompletes] = Arr.unzip(queue);
                    queue = [];
                    const onCompletesDef = onCompletes.filter(Predicate.isNotUndefined);
                    releaseUnsubscribedLoadingPromises();
                    db.mutate(mutations, [...subscribedQueries.keys()]).pipe(Effect.flatMap(handlePatches({ flushSync: onCompletesDef.length > 0 })), Effect.tap(() => {
                        onCompletesDef.forEach((onComplete) => onComplete());
                    }), runFork);
                });
            return { id };
        };
    })();
    const evolu = {
        socket,
        subscribeError: errorStore.subscribe,
        getError: errorStore.getState,
        createQuery: (queryCallback, options) => pipe(queryCallback(kysely).compile(), (compiledQuery) => {
            if (isSqlMutation(compiledQuery.sql))
                throw new Error("SQL mutation (INSERT, UPDATE, DELETE, etc.) isn't allowed in the Evolu `createQuery` function. Kysely suggests it because there is no read-only Kysely yet, and removing such an API is not possible. For mutations, use Evolu mutation API.");
            const parameters = compiledQuery.parameters;
            return {
                sql: compiledQuery.sql,
                parameters,
                ...(options && { options }),
            };
        }, (query) => serializeQuery(query)),
        loadQuery: (() => {
            let queue = [];
            return (query) => {
                Effect.logDebug([
                    "Evolu loadQuery",
                    { query: deserializeQuery(query) },
                ]).pipe(runSync);
                let loadingPromise = loadingPromises.get(query);
                if (!loadingPromise) {
                    let resolve = constVoid;
                    const promise = new Promise((_resolve) => {
                        resolve = _resolve;
                    });
                    loadingPromise = { resolve, promise, releaseOnResolve: false };
                    loadingPromises.set(query, loadingPromise);
                    queue = [...queue, query];
                    if (queue.length === 1) {
                        queueMicrotask(() => {
                            db.loadQueries(Arr.dedupe(queue)).pipe(Effect.flatMap(handlePatches()), runFork);
                            queue = [];
                        });
                    }
                }
                return loadingPromise.promise;
            };
        })(),
        loadQueries: (queries) => queries.map(evolu.loadQuery),
        subscribeQuery: (query) => (listener) => {
            subscribedQueries.set(query, Number.increment(subscribedQueries.get(query) ?? 0));
            const unsubscribe = rowsStore.subscribe(listener);
            return () => {
                const count = subscribedQueries.get(query);
                if (count != null && count > 1)
                    subscribedQueries.set(query, Number.decrement(count));
                else
                    subscribedQueries.delete(query);
                unsubscribe();
            };
        },
        getQuery: (query) => queryResultFromRows(rowsStore.getState().get(query) || emptyRows()),
        subscribeOwner: ownerStore.subscribe,
        getOwner: ownerStore.getState,
        subscribeSyncState: syncStateStore.subscribe,
        getSyncState: syncStateStore.getState,
        create: mutate,
        update: mutate,
        createOrUpdate: mutate,
        resetOwner: (options) => Effect.gen(function* () {
            yield* db.resetOwner();
            if (options?.reload !== false)
                yield* appStateReset.reset;
        }).pipe(runPromise),
        restoreOwner: (mnemonic, options) => Effect.gen(function* () {
            yield* db.restoreOwner(schema, mnemonic);
            if (options?.reload !== false)
                yield* appStateReset.reset;
        }).pipe(runPromise),
        reloadApp: () => {
            appStateReset.reset.pipe(runFork);
        },
        ensureSchema: (schema) => {
            db.ensureSchema(schema).pipe(runFork);
        },
        exportDatabase: () => db.exportDatabase().pipe(runPromise),
    };
    return evolu;
});
const initialDataToMutations = (initialData = constVoid) => Effect.map(NanoIdGenerator, (nanoIdGenerator) => {
    const mutations = [];
    const mutate = (table, { id, ...values }) => {
        if (id == null)
            id = nanoIdGenerator.rowId.pipe(Effect.runSync);
        mutations.push({ isInsert: true, id, table: table, values });
        return { id };
    };
    const evolu = {
        create: mutate,
        createOrUpdate: mutate,
    };
    initialData(evolu);
    return mutations;
});
// https://kysely.dev/docs/recipes/splitting-query-building-and-execution
const kysely = new Kysely.Kysely({
    dialect: {
        createAdapter: () => new Kysely.SqliteAdapter(),
        createDriver: () => new Kysely.DummyDriver(),
        createIntrospector() {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw "Not implemeneted";
        },
        createQueryCompiler: () => new Kysely.SqliteQueryCompiler(),
    },
});
const createIndex = kysely.schema.createIndex.bind(kysely.schema);
/**
 * Create SQLite indexes.
 *
 * See https://www.evolu.dev/docs/indexes
 *
 * @example
 *   const indexes = createIndexes((create) => [
 *     create("indexTodoCreatedAt").on("todo").column("createdAt"),
 *     create("indexTodoCategoryCreatedAt")
 *       .on("todoCategory")
 *       .column("createdAt"),
 *   ]);
 */
export const createIndexes = (callback) => callback(createIndex).map((index) => ({
    name: index.toOperationNode().name.name,
    sql: index.compile().sql,
}));
/** Create a namespaced lock name. */
export const getLockName = (name) => Effect.map(Config, (config) => `evolu:${config.name}:${name}`);
